interface DistributeConfigType {
  wait?: number;
  maxIdLength?: number;
  maxRetryCount?: number;
  parallelCount?: number;
}

export type ResponseItemType = { [props: string | number]: any };

interface DistributeMapType {
  [prop: string | number]: {
    promise?: Promise<any>;
    resolve: (data: ResponseItemType) => void;
    reject: (err: Error) => void;
  };
}

type DistributePromiseReturnType = (id: string, useCache?: boolean) => Promise<ResponseItemType>;
type RequestFuncType = (ids: string[]) => Promise<ResponseItemType>;

interface PromiseMergeMapType {
  [prop: string | number]: Promise<any>;
}

// distributePromise（合并请求分发结果）
// 第一个参数：包含批量接口调用异步操作的方法，接收批量id数组，返回resolve批量id对应结果的map对象。
// 第二个参数：{
//   防抖等待的时间wait
//   接口允许接收的批量id数组最大长度maxLength
//   接口返回失败最大重试次数maxRetryCount
//   接口并行请求数量
// }
// 返回：一个函数，这个函数接收想要请求的单个项id以及是否使用缓存useCache 返回单个id对应的请求结果。

export function distributePromise(
  fn: RequestFuncType,
  { wait, maxIdLength = 10, maxRetryCount = 5, parallelCount = 1 }: DistributeConfigType,
): DistributePromiseReturnType {
  const resultMap: ResponseItemType = {};
  const distributeMap: DistributeMapType = {};
  let promiseMergeId = 1;
  const promiseMergeMap: PromiseMergeMapType = {};
  let undoIds: string[] = [];
  let errRetryCount = 0;

  function merge() {
    // 判断最大并行数，和是否还有未处理的id；
    if (Object.values(promiseMergeMap).length <= parallelCount && undoIds.length) {
      const index = promiseMergeId;
      promiseMergeId++;
      promiseMergeMap[index] = new Promise((resolveMerge, reject) => {
        setTimeout(() => {
          if (!undoIds.length) {
            // 处理并行时，id处理完成的情况；
            resolveMerge({});
            return;
          }
          const ids = undoIds.splice(0, maxIdLength);
          // 请求去重
          fn([...new Set(ids)])
            .then(data => {
              Object.assign(resultMap, data);
              resolveMerge(data);
              ids.forEach(n => {
                distributeMap[n] && distributeMap[n].resolve(data[n]);
              });
            })
            .catch(err => {
              errRetryCount++;
              // 超过最大错误次数，则reject；否则就重新加入队列请求。
              if (errRetryCount > maxRetryCount) {
                ids.forEach(n => {
                  distributeMap[n] && distributeMap[n].reject(err);
                });
                setTimeout(() => {
                  errRetryCount = 0;
                }, 10);
              } else {
                undoIds = ids.concat(undoIds);
              }
              reject(err);
            });
        }, wait);
      }).finally(() => {
        delete promiseMergeMap[index];
        // 递归执行，如果队列中还有未执行的id则继续执行；
        merge();
      });
      merge();
    }
  }
  function resultFn(id: string, useCache?: boolean) {
    // 如果使用缓存，就不需要请求了；
    if (resultMap[id] && useCache) {
      return Promise.resolve(resultMap[id]);
    }
    // 分发promise，缓存去重
    if (distributeMap[id]) return distributeMap[id].promise;
    const promise = new Promise((resolveDistribute, rejectDistribute) => {
      undoIds.push(id);
      distributeMap[id] = {
        resolve(data) {
          resolveDistribute(data);
          distributeMap[id] && delete distributeMap[id];
        },
        reject(err) {
          rejectDistribute(err);
          distributeMap[id] && delete distributeMap[id];
        },
      };
      merge();
    });
    distributeMap[id].promise = promise;
    return promise;
  }
  return resultFn as DistributePromiseReturnType;
}

// export const add2 = distributePromise(
//   (ids: string[]) => new Promise((resolve) => {
//     setTimeout(() => {
//       console.log(5555, ids);
//       const data: { [prop: string | number]: any } = {};
//       ids.forEach((n) => { data[n] = `${n}add`; });
//       resolve(data);
//     }, 1000);
//   }),
//   {
//     wait: 1000,
//     maxIdLength: 2,
//     parallelCount: 2,
//   },
// );

// add2('11').then((s) => console.log(s));
// add2('11').then((s) => console.log(s));
// add2('13').then((s) => console.log(s));
// add2('12').then((s) => console.log(s));
// add2('13').then((s) => console.log(s));
// add2('14').then((s) => console.log(s));
// add2('15').then((s) => console.log(s));
// add2('13').then((s) => console.log(s));

// setTimeout(() => {
//   add2('200').then((s) => console.log(s));
// }, 200);

// setTimeout(() => {
//   add2('13', true).then((s) => console.log(s));
//   add2('3900').then((s) => console.log(s));
// }, 3900);

// setTimeout(() => {
//   console.log('3秒后');
//   add2('11').then((s) => console.log(s));
//   add2('2000').then((s) => console.log(s));
//   add2('2001').then((s) => console.log(s));
// }, 3000);
